/*
 * Promo Up'n Down — le mouvement.
 *
 * Deux règles de vie :
 *   1. Le réglage système « mouvement réduit » gagne toujours.
 *   2. Rien ne s'anime pendant qu'on saisit du texte.
 *
 * Aucune dépendance, aucun appel réseau, aucune lecture de données client.
 * Le fichier ne fait que deux choses : poser des classes CSS au défilement,
 * et compter des chiffres.
 *
 * Si ce script ne démarre jamais, la page reste entièrement lisible : la classe
 * updn-js n'est posée qu'ici, et c'est elle qui déclenche les états de départ.
 */
(function () {
  'use strict';

  var racine = document.documentElement;

  function mouvementReduit() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* On saisit du texte ? On ne bouge rien. */
  function enSaisie() {
    var a = document.activeElement;
    if (!a) return false;
    var t = a.tagName;
    return t === 'INPUT' || t === 'TEXTAREA' || t === 'SELECT' || a.isContentEditable;
  }

  racine.classList.add('updn-js');

  /* 1. Révélation au défilement.
     Un seul observateur pour toute la page. Chaque élément se révèle une fois
     puis on cesse de l'observer — pas de travail résiduel au défilement.
     Le décalage se lit sur l'attribut, il n'est pas calculé au hasard.

     Le seuil est à 0, pas à 0,12 : une section plus haute que l'écran ne peut
     jamais atteindre 12 % de recouvrement, et resterait invisible à jamais.
     La marge basse de -12 % suffit à déclencher au bon moment. */
  function revelerAuDefilement() {
    var cibles = document.querySelectorAll('[data-updn-reveler]');
    if (!cibles.length) return;

    if (mouvementReduit() || !('IntersectionObserver' in window)) {
      cibles.forEach(function (el) { el.classList.add('est-revele'); });
      return;
    }

    var observateur = new IntersectionObserver(function (entrees) {
      entrees.forEach(function (entree) {
        if (!entree.isIntersecting) return;
        var el = entree.target;
        var rang = parseInt(el.getAttribute('data-updn-rang') || '0', 10);
        // Une CLASSE, pas une variable CSS. Une variable s'hérite : l'écrire
        // invalidait le style de toute la descendance du bloc, et le moteur
        // recalculait trente sous-arbres au fil du défilement. Mesuré sur
        // l'accueil : 152 ms de recalcul en moins, un tiers du coût total.
        //
        // Plafonné à 6 : au-delà de 360 ms le décalage ne se distingue plus, et
        // il faudrait une classe par rang jusqu'à l'infini.
        if (rang > 0) el.classList.add('updn-d' + Math.min(rang, 6));
        el.classList.add('est-revele');
        observateur.unobserve(el);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0 });

    cibles.forEach(function (el) { observateur.observe(el); });
  }

  /* 2. Compteurs.
     On compte depuis 0 une seule fois, à l'entrée à l'écran. La valeur finale
     est lue sur l'attribut : le texte du gabarit reste la source de vérité et
     le chiffre est correct même sans JS. */
  function animerNombre(el) {
    var cible = parseFloat(el.getAttribute('data-updn-compteur'));
    if (isNaN(cible)) return;

    var decimales = parseInt(el.getAttribute('data-updn-decimales') || '0', 10);
    var avant = el.getAttribute('data-updn-avant') || '';
    var apres = el.getAttribute('data-updn-apres') || '';
    var duree = 1400;
    var debut = null;

    function ecrire(v) {
      el.textContent = avant + v.toFixed(decimales).replace('.', ',') + apres;
    }

    function pas(horodatage) {
      if (debut === null) debut = horodatage;
      var t = Math.min((horodatage - debut) / duree, 1);
      /* easeOutExpo : le chiffre file puis se pose, il ne freine pas mollement. */
      var e = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
      ecrire(cible * e);
      if (t < 1) requestAnimationFrame(pas);
      else ecrire(cible);
    }

    if (mouvementReduit()) { ecrire(cible); return; }
    requestAnimationFrame(pas);
  }

  function surveillerCompteurs() {
    var cibles = document.querySelectorAll('[data-updn-compteur]');
    if (!cibles.length) return;

    if (!('IntersectionObserver' in window)) {
      cibles.forEach(animerNombre);
      return;
    }

    var observateur = new IntersectionObserver(function (entrees) {
      entrees.forEach(function (entree) {
        if (!entree.isIntersecting) return;
        animerNombre(entree.target);
        observateur.unobserve(entree.target);
      });
    }, { threshold: 0.5 });

    cibles.forEach(function (el) { observateur.observe(el); });
  }

  /* 3. Header à deux états + barre de progression.
     Transparent par-dessus un hero de marque, condensé dès 80 px.
     Tout passe par des classes posées sur le header lui-même : aucune ligne de
     style n'est écrite dans le DOM du thème, et le header Liquid reste intact. */
  function surveillerEntete() {
    var enveloppe = document.querySelector('.header-wrapper');
    if (!enveloppe) return;

    var hero = document.querySelector('[data-updn-hero]');

    /* On mesure la hauteur du header avant de poser la classe : les deux
       atterrissent dans le même recalcul de style, donc le hero ne saute pas
       au chargement.

       La variable vit sur <html> parce que le hero et les filtres de
       collection en ont besoin. Une variable posée sur la racine invalide le
       style du document ENTIER : on ne la réécrit donc que si elle change
       vraiment. Les observateurs plus bas appellent cette fonction chaque fois
       que la page bouge, et la valeur est alors presque toujours identique. */
    var hauteurPosee = null;
    function mesurerEntete() {
      var h = enveloppe.offsetHeight;
      if (h === hauteurPosee) return;
      hauteurPosee = h;
      racine.style.setProperty('--updn-hauteur-entete', h + 'px');
    }
    mesurerEntete();

    /* Deux choses différentes portaient le même nom, et c'est ce qui coûtait
       cher. « Le hero remonte sous le header » est une décision de MISE EN
       PAGE : elle vaut tant qu'il y a un hero, quel que soit le défilement.
       « Le header est transparent » est une APPARENCE, elle, qui suit le
       défilement.

       Confondues, elles formaient une boucle : la classe ajoutait un
       `padding-top` au hero, donc changeait son `offsetHeight`, donc `finDuHero`,
       donc la classe rebasculait — et chaque bascule, posée sur <html>,
       recalculait le style de toute la page. Mesuré sur l'accueil, section
       « Nos ensembles à rabais » : 252 ms de traversée contre 83 sans elles.

       Séparées, la mise en page ne bouge plus, et l'apparence se joue sur
       l'entête seule — son sous-arbre, pas le document. */
    if (hero) racine.classList.add('updn-a-hero');
    if (hero) enveloppe.classList.add('est-sur-hero');

    var barre = document.createElement('div');
    barre.className = 'updn-progression';
    barre.setAttribute('aria-hidden', 'true');
    enveloppe.appendChild(barre);

    var enAttente = false;

    /* Ces trois mesures obligent le navigateur à recalculer la mise en page.
       Les relire à chaque image du défilement, c'est la garantie de saccader.
       On les prend une fois, et on les reprend quand la page change de taille. */
    var courseTotale = 0, finDuHero = 0;

    function remesurerLaPage() {
      courseTotale = document.documentElement.scrollHeight - window.innerHeight;
      if (hero) finDuHero = hero.offsetTop + hero.offsetHeight - enveloppe.offsetHeight - 24;
    }

    function mesurer() {
      var y = window.scrollY || window.pageYOffset;

      enveloppe.classList.toggle('est-collee', y > 80);

      /* On quitte l'état transparent juste avant la fin du hero, pas après :
         le texte blanc du menu doit repasser en encre pendant qu'il est
         encore sur fond sombre. */
      if (hero) enveloppe.classList.toggle('est-sur-hero', y < finDuHero);

      barre.style.transform = 'scaleX(' + (courseTotale > 0 ? Math.min(y / courseTotale, 1) : 0) + ')';

      enAttente = false;
    }

    function auDefilement() {
      if (enAttente) return;
      enAttente = true;
      requestAnimationFrame(mesurer);
    }

    window.addEventListener('scroll', auDefilement, { passive: true });
    window.addEventListener('resize', function () {
      mesurerEntete(); remesurerLaPage(); auDefilement();
    }, { passive: true });

    /* La page grandit après le chargement : images qui arrivent, blocs
       d'applications qui se déplient. On reprend la mesure sans rien bloquer.

       Deux observateurs, et il faut les deux. Celui du header parce que sa
       hauteur n'est pas connue au premier calcul : le menu se replie sur deux
       lignes quand la police d'affichage arrive, et le header passe de 111 à
       140 px. Sans lui, le hero reste remonté de l'ancienne valeur et laisse
       une bande blanche sous la barre d'annonce — c'est arrivé, c'est visible.
       Celui du corps pour la course totale de la barre de progression.

       Le coût est nul : `mesurerEntete` ne réécrit la variable de racine que
       si la hauteur a vraiment changé. */
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { mesurerEntete(); remesurerLaPage(); }).observe(enveloppe);
      new ResizeObserver(function () { remesurerLaPage(); }).observe(document.body);
    }
    window.addEventListener('load', function () { mesurerEntete(); remesurerLaPage(); });

    remesurerLaPage();
    mesurer();
  }

  /* 4. Parallaxe du hero : 8 % de course, jamais davantage.
     Coupée sous 750 px et quand le mouvement est réduit. */
  function surveillerParallaxe() {
    var fond = document.querySelector('.updn-hero__fond');
    if (!fond || mouvementReduit()) return;
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    var hero = fond.closest('[data-updn-hero]') || fond.parentElement;
    var enAttente = false;
    var hautDuHero = 0, hauteurDuHero = 0;

    /* Même principe que pour le header : la géométrie du hero se mesure une
       fois, pas soixante fois par seconde. */
    function remesurerLeHero() {
      hautDuHero = hero.offsetTop;
      hauteurDuHero = hero.offsetHeight;
    }

    function mesurer() {
      var y = window.scrollY || window.pageYOffset;
      var haut = hautDuHero - y;
      if (haut + hauteurDuHero > 0 && haut < window.innerHeight) {
        var avancee = Math.min(Math.max(-haut / hauteurDuHero, 0), 1);
        fond.style.setProperty('--updn-parallaxe', (avancee * hauteurDuHero * 0.08).toFixed(1) + 'px');
      }
      enAttente = false;
    }

    window.addEventListener('scroll', function () {
      if (enAttente || enSaisie()) return;
      enAttente = true;
      requestAnimationFrame(mesurer);
    }, { passive: true });
    window.addEventListener('resize', function () { remesurerLeHero(); mesurer(); }, { passive: true });

    remesurerLeHero();
    mesurer();
  }

  /* 5. La pastille du panier qui bat quand un article s'ajoute. */
  function surveillerPanier() {
    document.addEventListener('cart:updated', function () {
      var pastille = document.querySelector('.cart-count-bubble');
      if (!pastille || mouvementReduit()) return;
      pastille.classList.remove('est-battue');
      void pastille.offsetWidth;
      pastille.classList.add('est-battue');
    });
  }

  function demarrer() {
    revelerAuDefilement();
    surveillerCompteurs();
    surveillerEntete();
    surveillerParallaxe();
    surveillerPanier();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }

  /* L'éditeur de thème remplace des sections à chaud : on rebranche. */
  document.addEventListener('shopify:section:load', demarrer);
})();
